import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GitCompare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { CompareSelector, ProgressiveComparisonView } from '@/components/v11/compare';
import {
  ComparisonDimensionPickerModal,
  ComparisonValuePickerModal,
} from '@/components/v11/compare/modals';
import type { ValueOption } from '@/components/v11/compare/modals';
import { useProgressiveCompare } from '@/hooks/useProgressiveCompare';
import {
  useDebouncedSubjectSearch,
  getSearchResults,
} from '@/hooks/useSubjectSearch';
import { useSubjectLists, getListItems } from '@/hooks/useSubjectLists';
import { funnelService } from '@/services/v11/funnelService';
import { getDimensionConfig } from '@/config/comparisonDimensions';
import type {
  SubjectSelection,
  DimensionTypeConfig,
  SearchableDimensionType,
  ListDimensionType,
  SearchResultItem,
  ListResultItem,
} from '@/types/comparison';

/**
 * V11 Felton Compare Page
 *
 * Multi-dimensional comparison feature allows comparing any two subjects across 12 dimension types:
 * - Searchable: Author, Brand, Team, Hashtag, Channel, Post
 * - Fixed Lists: Platform, Country, Age Group, Emotion, Category, Persona (saved segments)
 *
 * Architecture:
 * - Each dimension resolves to author_ids
 * - Same aggregation pipeline for all dimensions
 * - Results displayed with metrics cards, overlap visualization, and section comparisons
 *
 * Examples:
 * - Brand vs Brand: Nike vs Adidas
 * - Team vs Team: Lakers vs Warriors
 * - Platform vs Platform: Instagram vs TikTok
 * - Cross-type: Nike (brand) vs Lakers (team fanbase)
 */
const EMPTY_SELECTION: SubjectSelection = {
  type: null,
  item: null,
}

export const FeltonCompare: React.FC = () => {
  // Subject selections for A and B
  const [subjectA, setSubjectA] = useState<SubjectSelection>(EMPTY_SELECTION);
  const [subjectB, setSubjectB] = useState<SubjectSelection>(EMPTY_SELECTION);

  // Modal states
  const [activeSubject, setActiveSubject] = useState<'A' | 'B' | null>(null);
  const [dimensionModalOpen, setDimensionModalOpen] = useState(false);
  const [valueModalOpen, setValueModalOpen] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState<DimensionTypeConfig | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Determine if selected dimension is searchable or list
  const isSearchable = selectedDimension?.searchable ?? false;
  const isChannelDimension = selectedDimension?.type === 'channel';
  const isPostDimension = selectedDimension?.type === 'post';
  const isPreloadedDimension = isChannelDimension || isPostDimension;

  // Fetch search results (for searchable dimensions)
  const searchQuery_ = useDebouncedSubjectSearch(
    (selectedDimension?.type as SearchableDimensionType) || 'brand',
    searchQuery,
    300,
    {
      enabled: isSearchable && valueModalOpen && (isPreloadedDimension || searchQuery.length >= 2),
      minQueryLength: isPreloadedDimension ? 0 : 2,
    }
  );

  // Fetch list items (for list dimensions)
  const listQuery = useSubjectLists(
    (selectedDimension?.type as ListDimensionType) || 'platform',
    { enabled: !isSearchable && valueModalOpen }
  );

  // Extract values for ValuePickerModal
  const values: ValueOption[] = isSearchable
    ? getSearchResults(searchQuery_.data).map((item) => ({
        id: item.id,
        name: item.name,
        subtitle: item.subtitle,
        platform: item.platform,
        count: item.count,
      }))
    : getListItems(listQuery.data).map((item) => ({
        id: item.id,
        name: item.name,
        subtitle: item.subtitle,
      }));

  // Build comparison subjects from selections
  const comparisonSubjectA =
    subjectA && subjectA.type && subjectA.item
      ? { type: subjectA.type, id: subjectA.item.id }
      : null;
  const comparisonSubjectB =
    subjectB && subjectB.type && subjectB.item
      ? { type: subjectB.type, id: subjectB.item.id }
      : null;

  // Include Own Channels toggle (URL-persisted)
  const [searchParams, setSearchParams] = useSearchParams();
  const includeOwnChannels = searchParams.get('ioc') === '1';
  const setIncludeOwnChannels = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('ioc', '1');
      } else {
        next.delete('ioc');
      }
      return next;
    });
  }, [setSearchParams]);

  // Progressive comparison: fires individual + combined queries independently
  const progressiveState = useProgressiveCompare(comparisonSubjectA, comparisonSubjectB, includeOwnChannels);

  // Handle opening dimension picker for a subject
  const handleOpenDimensionPicker = (subject: 'A' | 'B') => {
    setActiveSubject(subject);
    setDimensionModalOpen(true);
  };

  // Handle dimension selection
  const handleDimensionSelect = (dimension: DimensionTypeConfig) => {
    setSelectedDimension(dimension);
    setDimensionModalOpen(false);
    setSearchQuery('');
    setValueModalOpen(true);
  };

  // Handle value selection
  const handleValueSelect = (value: SearchResultItem | ListResultItem) => {
    const selection: SubjectSelection = {
      type: selectedDimension?.type ?? null,
      item: value,
    };

    if (activeSubject === 'A') {
      setSubjectA(selection);
    } else if (activeSubject === 'B') {
      setSubjectB(selection);
    }

    setValueModalOpen(false);
    setSelectedDimension(null);
    setSearchQuery('');
    setActiveSubject(null);
  };

  // Handle closing dimension modal
  const handleCloseDimensionModal = () => {
    setDimensionModalOpen(false);
    setSelectedDimension(null);
    setActiveSubject(null);
  };

  // Handle closing value modal
  const handleCloseValueModal = () => {
    setValueModalOpen(false);
    setSearchQuery('');
    // Don't reset selectedDimension - user might want to go back
  };

  // Handle clear all
  const handleClearAll = () => {
    setSubjectA(EMPTY_SELECTION);
    setSubjectB(EMPTY_SELECTION);
  };

  // Check if any subject is selected (for conditional rendering)
  const hasAnySelection = (subjectA.type && subjectA.item) || (subjectB.type && subjectB.item);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
          <GitCompare className="h-6 w-6 text-purple-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-v11-text-primary">
            Multi-Dimensional Comparison
          </h1>
          <p className="text-sm text-v11-text-muted">
            Compare any two dimensions: brands, teams, platforms, and more
          </p>
        </div>
      </div>

      {/* Show Own Channels toggle */}
      <div className="flex items-center gap-1.5">
        <Switch
          id="compare-include-own-channels"
          checked={includeOwnChannels}
          onCheckedChange={setIncludeOwnChannels}
          className="scale-75"
        />
        <span
          className="text-xs text-v11-text-secondary cursor-pointer select-none"
          onClick={() => setIncludeOwnChannels(!includeOwnChannels)}
        >
          Show Own Channels
        </span>
      </div>

      {/* Subject Selectors - Horizontal Layout */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Subject A Selector */}
        <CompareSelector
          variant="A"
          subject={subjectA}
          onClick={() => handleOpenDimensionPicker('A')}
        />

        {/* VS Badge */}
        <div className="px-4 py-2 bg-purple-500/15 text-purple-400 text-sm font-bold rounded-full border border-purple-500/30">
          VS
        </div>

        {/* Subject B Selector */}
        <CompareSelector
          variant="B"
          subject={subjectB}
          onClick={() => handleOpenDimensionPicker('B')}
        />

        {/* Clear All Button */}
        {hasAnySelection && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="ml-auto text-muted-foreground hover:text-destructive"
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Results Section */}
      <div className="pt-4">
        {/* Progressive Comparison View - handles all states internally */}
        {hasAnySelection ? (
          <ProgressiveComparisonView state={progressiveState} />
        ) : (
          /* Empty State - no subjects selected */
          <div className="bg-v11-surface border border-v11-border rounded-lg p-12 text-center">
            <GitCompare className="h-16 w-16 text-v11-text-muted mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-semibold text-v11-text-primary mb-2">
              Ready to Compare
            </h3>
            <p className="text-sm text-v11-text-muted max-w-md mx-auto">
              Select two subjects above to compare their audience characteristics, demographics,
              engagement metrics, and more.
            </p>
            <div className="mt-6 space-y-2 text-xs text-v11-text-muted">
              <p className="font-medium">Examples:</p>
              <ul className="space-y-1">
                <li>• Nike vs Adidas (Brand comparison)</li>
                <li>• Lakers vs Warriors (Team comparison)</li>
                <li>• Instagram vs TikTok (Platform comparison)</li>
                <li>• Nike vs Lakers (Cross-type: brand vs team fanbase)</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ComparisonDimensionPickerModal
        open={dimensionModalOpen}
        onClose={handleCloseDimensionModal}
        onSelectDimension={handleDimensionSelect}
      />

      {selectedDimension && (
        <ComparisonValuePickerModal
          open={valueModalOpen}
          onClose={handleCloseValueModal}
          dimensionLabel={selectedDimension.label}
          values={values}
          isLoading={isSearchable ? searchQuery_.isLoading : listQuery.isLoading}
          searchMode={isSearchable}
          onSearch={setSearchQuery}
          isSearching={searchQuery_.isFetching}
          onApply={handleValueSelect}
          preloaded={isPreloadedDimension}
        />
      )}
    </div>
  );
};
